import { useEffect, useState } from "react";
import {
  MemoryRouter,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";

import i18next from "i18next";

import { ThemeProvider } from "@/providers/theme-provider"
import { initializeGA4 } from "@/lib/ga-loader";

import { DigitalAsset } from "./models/continente-credentials/common/DigitalAsset";
import {
  DigitalAssetContext,
  defaultDigitalAsset,
} from "./contexts/continente-credentials/DigitalAssetContext";

import { User } from "./models/continente-credentials/common/User";
import {
  UserContext,
  defaultUser,
} from "./contexts/continente-credentials/UserContext";

import SdkApp from "./SdkApp";
import SocialLoginApp from "./SocialLoginApp";
import DevApp from "./DevApp";
import SiteCredenciaisApp from "./SiteCredenciaisApp";

import ScrollToTop from "./ScrollToTop";

import DefaultLayout from "./components/layout/DefaultLayout";
import SidePanelLayout from "./components/layout/SidePanelLayout";

function changeLanguage(language?: string) {
  i18next.changeLanguage(language?.toLowerCase());
}

function detectIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

interface AppRouterProps {
  inIframe?: boolean;
  children: React.ReactNode;
}

const AppRouter: React.FC<AppRouterProps> = ({
  inIframe = detectIframe(),
  children,
}) => {
  if (inIframe) {
    return (
      <MemoryRouter
        initialEntries={[window.location.pathname + window.location.search]}
      >
        {children}
      </MemoryRouter>
    );
  }

  return <Router>{children}</Router>;
};

export default function App() {
  const [digitalAsset, setDigitalAsset] = useState<DigitalAsset>(defaultDigitalAsset);
  const [user, setUser] = useState<User>(defaultUser);
  const showDev = false;

  const updateDigitalAsset = (newDigitalAsset: DigitalAsset) => {
    setDigitalAsset(newDigitalAsset);
  };

  const updateUser = (newUser: User) => {
    setUser(newUser);
  };

  useEffect(() => {
    if (digitalAsset.googleAnalyticsEnabled) {
      initializeGA4();
    }
  }, [digitalAsset.googleAnalyticsEnabled]);

  const showSidePanel = digitalAsset.responsiveModeEnabled === true && !!digitalAsset.sidePanelImageUrl;

  const query = new URLSearchParams(window.location.search);
  const language = query.get("language") || undefined;
  changeLanguage(language);

  const routes = (
    <>
      <ScrollToTop />
      <Switch>
        <SocialLoginApp />
      </Switch>
      <Switch>
        <SdkApp />
      </Switch>
      <Switch>
        <SiteCredenciaisApp />
      </Switch>
      {showDev && (
        <Switch>
          <DevApp />
        </Switch>
      )}
    </>
  );

  return (
    <AppRouter>
      <UserContext.Provider
        value={{
          user: user,
          updateUser: updateUser,
        }}
      >
        <DigitalAssetContext.Provider
          value={{
            digitalAsset: digitalAsset,
            updateDigitalAsset: updateDigitalAsset,
          }}
        >
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            {
              showSidePanel ? (
                <SidePanelLayout
                  imageUrl={digitalAsset.sidePanelImageUrl!}
                  backgroundColor={digitalAsset.sidePanelBackgroundColor}
                >
                  {routes}
                </SidePanelLayout>
              ) : (
                <DefaultLayout>{routes}</DefaultLayout>
              )
            }
          </ThemeProvider>
        </DigitalAssetContext.Provider>
      </UserContext.Provider>
    </AppRouter >
  );
}
